import React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, EnhancedAccordian } from '../../../../../../ui';
import { AddQuestionModal } from '../../../../../../ui/add_question_modal/add_question_modal';
import { PencilIcon, TrashIcon } from '../../../../../../ui/icons/icons';
import { Modal } from '../../../../../../ui/modal/modal';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import {
  Question,
  SubSkill,
} from '../../../../../../utils/types/assessment_types';

import { AutoAssessment } from '../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';
import { DIALOG_TYPE } from '../../../../Constants/DialogTypes';
import { DropdownWithModal } from '../../../ui/DropdownWithModal';
import { SaveTemplate } from './Components';
import { TemplateDrawer } from './Components/TemplateDrawer';
import { QuickBuildDrawer } from './Containers';
import { SelectedQuestionContent } from './Containers/Components';
import SelectQuestions from './Containers/SelectQuestions';
import { useQuestions } from './Hooks';

interface IQuestions {
  assessmentId: string;
  skillList?: SelectOptions[];
  levelList?: SelectOptions[];
  jobTitleList?: SelectOptions[];
  subSkillList?: SubSkill[];
  setSkillList: (opts: SelectOptions[]) => void;
  jobTitle: string;
  jobTitleId: string;
  completedElement: () => void;
  setAutoAssessment: React.Dispatch<React.SetStateAction<AutoAssessment>>;
  autoAssessment: AutoAssessment;
  skills: string[];
}

export const Questions: React.FC<IQuestions> = ({
  assessmentId,
  skillList,
  levelList,
  jobTitleList,
  subSkillList,
  setSkillList,
  jobTitle,
  jobTitleId,
  completedElement,
  setAutoAssessment,
  autoAssessment,
  skills,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const SQT = ADMIN_PAGES_KEYS.jobOpenings.skillQuestionsTab;
  const {
    expandedIds,
    questionList,
    editQuestion,
    deleteQuestion,
    handleExpandAll,
    handleOpenSelectQuestions,
    handleDragEnd,
    toggleExpand,
    isOpenSelectQuestions,
    handleCloseSelectQuestions,
    handleAddQuestion,
    selectedQuestions,
    handleSaveSelection,
    totalCount,
    setIsTemplateDrawerOpen,
    setIsTemplateCreateOpen,
    setIsModalOpen,
    toggleAddQuestionModal,
    setDisabled,
    editMap,
    questionTypes,
    isModalOpen,
    handleCloseModal,
    handleDeleteTemplate,
    addQuestionModalOpen,
    addNewQuestion,
    setAddQuestionModalOpen,
    isTemplateDrawerOpen,
    handleTemplateSelect,
    isTemplateCreateOpen,
    handleTemplateSave,
    isOpenQuickBuild,
    handleOpenQuickBuild,
    handleCloseQuickBuild,
    fetchQuestions,
  } = useQuestions(assessmentId, completedElement);

  return (
    <>
      { /* Modern Header */ }
      <div className='pb-4 sm:pb-6'>
        <div className='flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4'>
          { /* Title & Stats Group */ }
          <div className='flex w-full flex-col gap-3 lg:w-auto'>
            <h2 className='text-lg font-semibold text-gray-900 sm:text-xl'>
              { t(SQT.heading) }
            </h2>

            <div className='flex flex-wrap gap-2 sm:gap-3 items-center text-xs sm:text-sm'>
              <div className='px-2.5 py-1.5 sm:px-3 bg-blue-50 text-blue-700 rounded-lg border border-blue-100 font-medium flex items-center gap-2'>
                <span className='text-blue-400'>
                  <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                  >
                    <path d='M12 20v-6M6 20V10M18 20V4' />
                  </svg>
                </span>
                { t(SQT.score) } { totalCount.totalPoints }
              </div>

              <div className='px-2.5 py-1.5 sm:px-3 bg-amber-50 text-amber-700 rounded-lg border border-amber-100 font-medium flex items-center gap-2'>
                <span className='text-amber-400'>
                  <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                  >
                    <circle cx='12' cy='12' r='10' />
                    <polyline points='12 6 12 12 16 14' />
                  </svg>
                </span>
                { t(SQT.duration) } { totalCount.totalTime } min
              </div>

              <div className='px-2.5 py-1.5 sm:px-3 bg-purple-50 text-purple-700 rounded-lg border border-purple-100 font-medium flex items-center gap-2'>
                <span className='text-purple-400'>
                  <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                  >
                    <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                    <polyline points='14 2 14 8 20 8' />
                    <line x1='16' y1='13' x2='8' y2='13' />
                    <line x1='16' y1='17' x2='8' y2='17' />
                    <polyline points='10 9 9 9 8 9' />
                  </svg>
                </span>
                { questionList.length } { t(SQT.questions) }
              </div>
            </div>
          </div>

          { /* Action Buttons */ }
          <div className='grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:justify-end sm:gap-3 lg:self-center'>
            <DropdownWithModal
                label={t(SQT.actions)}
                className='col-span-2 sm:col-span-1 sm:min-w-[120px]'
                buttonColor='gray'
                options={[
                  {
                    label: t(SQT.loadTemplate),
                    onClick: () => setIsTemplateDrawerOpen(true),
                    icon: (
                      <svg
                          className='w-4 h-4 text-gray-500'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
                        />
                      </svg>
                    ),
                    value: 'load-template',
                  },
                  {
                    label: t(SQT.createTemplate),
                    onClick: () => setIsTemplateCreateOpen(true),
                    icon: (
                      <svg
                          className='w-4 h-4 text-gray-500'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4'
                        />
                      </svg>
                    ),
                    value: 'create-template',
                  },
                  {
                    label: t(SQT.removeAllQuestions),
                    onClick: () => {
                      setIsModalOpen(true);
                    },
                    icon: (
                      <svg
                          className='w-4 h-4 text-red-500'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                        />
                      </svg>
                    ),
                    value: 'remove-all',
                    hoverStyle: 'red',
                  },
                  {
                    label:
                    expandedIds.length === questionList.length
                      ? t(SQT.collapseAll)
                      : t(SQT.expandAll),
                    onClick: handleExpandAll,
                    icon: (
                      <svg
                          className='w-4 h-4 text-gray-500'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        { expandedIds.length === questionList.length ? (
                          <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M19 9l-7-7-7 7m14 14l-7-7-7 7'
                          />
                        ) : (
                          <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M5 15l7-7 7 7m-14-4l7 7 7-7'
                          />
                        ) }
                      </svg>
                    ),
                    value: 'toggle-expand',
                  },
                ]}
            />
            <Button
                label={t(SQT.selectQuestions)}
                onClick={handleOpenSelectQuestions}
                variant='link'
                className='w-full bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 shadow-sm sm:w-auto'
            />{ ' ' }
            <Button
                label={t(SQT.quickBuild)}
                onClick={handleOpenQuickBuild}
                variant='link'
                className='w-full shadow-md hover:shadow-lg transition-shadow sm:w-auto'
            />
            <Button
                label={t(SQT.createNew)}
                onClick={toggleAddQuestionModal}
                variant='primary'
                className='col-span-2 w-full shadow-md hover:shadow-lg transition-shadow sm:col-span-1 sm:w-auto'
            />
          </div>
        </div>
      </div>
      { /* List */ }
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='questions'>
          { (prov) => (
            <div {...prov.droppableProps} ref={prov.innerRef}>
              { questionList.map((q, i) => {
                const isDisabled = editMap[q.id] ?? true;

                const onEditSave = (updated: Question) => {
                  setDisabled(q.id, true);
                  editQuestion(updated);
                };

                const returnSelectedJobTitles = () =>
                  (q.jobTitle ?? [])
                    .map((id) => {
                      const found = jobTitleList?.find((j) => j.value === id);
                      return found
                        ? { value: found.value, label: found.label }
                        : null;
                    })
                    .filter((x): x is SelectOptions => Boolean(x));

                return (
                  <Draggable key={q.id} draggableId={q.id} index={i}>
                    { (p) => (
                      <div
                          ref={p.innerRef}
                          {...p.draggableProps}
                          {...p.dragHandleProps}
                          className='mb-2'
                      >
                        <EnhancedAccordian
                            title={q.question}
                            isExpanded={expandedIds.includes(q.id)}
                            onToggle={() => toggleExpand(q.id)}
                            rightIcons={[
                              <button
                                  key='edit'
                                  type='button'
                                  className='p-1 text-gray-500 hover:text-gray-700'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDisabled(q.id, false);
                                    if (!expandedIds.includes(q.id))
                                      toggleExpand(q.id);
                                  }}
                              >
                                <PencilIcon className='w-4 h-4' />
                              </button>,
                              <button
                                  key='delete'
                                  type='button'
                                  className='p-1 text-gray-500 hover:text-red-500'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    q.assessmentQuestionId &&
                                  deleteQuestion(q.assessmentQuestionId);
                                  }}
                              >
                                <TrashIcon className='w-4 h-4' />
                              </button>,
                            ]}
                        >
                          <SelectedQuestionContent
                              toggledQuestion={q}
                              isDisabled={isDisabled}
                              setIsDisabled={(d) => setDisabled(q.id, d)}
                              editQuestion={onEditSave}
                              onAddNewSkill={() => {}}
                              returnSelectedJobTitles={returnSelectedJobTitles}
                              skills={skillList || []}
                              subSkills1={
                                subSkillList
                                  ?.filter((s) => s.parentSkillId === q.skillId)
                                  .map((s) => ({
                                    value: s.id,
                                    label: s.subskillName,
                                  })) || []
                              }
                              subSkills2={
                                subSkillList
                                  ?.filter(
                                    (s) => s.parentSkillId === q.subSkill1Id,
                                  )
                                  .map((s) => ({
                                    value: s.id,
                                    label: s.subskillName,
                                  })) || []
                              }
                              subSkills3={
                                subSkillList
                                  ?.filter(
                                    (s) => s.parentSkillId === q.subSkill2Id,
                                  )
                                  .map((s) => ({
                                    value: s.id,
                                    label: s.subskillName,
                                  })) || []
                              }
                              levelList={levelList}
                              jobTitleList={jobTitleList || []}
                              questionTypes={questionTypes}
                          />
                        </EnhancedAccordian>
                      </div>
                    ) }
                  </Draggable>
                );
              }) }
              { prov.placeholder }
            </div>
          ) }
        </Droppable>
      </DragDropContext>
      <SelectQuestions
          isOpen={isOpenSelectQuestions}
          onCloseDrawer={handleCloseSelectQuestions}
          onSave={handleSaveSelection}
          onAddQuestion={handleAddQuestion}
          initialSelected={selectedQuestions}
          assessmentId={assessmentId}
          skillList={skillList}
          levelList={levelList}
          subSkillList={subSkillList}
          jobTitleList={jobTitleList}
      />
      <QuickBuildDrawer
          assessmentId={assessmentId}
          isOpen={isOpenQuickBuild}
          onCloseDrawer={handleCloseQuickBuild}
          autoAssessment={autoAssessment}
          setAutoAssessment={setAutoAssessment}
          skills={skills}
          jobTitle={jobTitle}
          completedElement={completedElement}
          onGenerationComplete={fetchQuestions}
      />
      <Dialog
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={t(SQT.deleteAllConfirm)}
          dialogType={DIALOG_TYPE.ERROR}
          justify='center'
          confirmButtonLable={t(SQT.deleteAll)}
          cancelButtonLable={t(SQT.cancel)}
          handleApply={handleDeleteTemplate}
      >
        <></>
      </Dialog>
      <Modal
          visible={addQuestionModalOpen}
          modalContent={
            addQuestionModalOpen ? (
              <AddQuestionModal
                  addNewQuestion={addNewQuestion}
                  skillList={skillList || []}
                  levelList={levelList || []}
                  jobTitleList={jobTitleList || []}
                  subSkillList={subSkillList}
                  setSkillList={setSkillList}
              />
            ) : (
              <></>
            )
          }
          toggle={() => setAddQuestionModalOpen(false)}
      />
      <TemplateDrawer
          isOpen={isTemplateDrawerOpen}
          onClose={() => setIsTemplateDrawerOpen(false)}
          onSelectTemplate={handleTemplateSelect}
      />
      <Modal
          headerText={t(SQT.saveTemplate)}
          visible={isTemplateCreateOpen}
          modalContent={
            <SaveTemplate
                closeModal={() => setIsTemplateCreateOpen(false)}
                onTemplateSave={handleTemplateSave}
                questions={questionList}
                jobTitle={jobTitle}
                jobTitleId={jobTitleId}
                assessmentSettings={{
                  attemptCount: '1',
                  customMessage: '',
                  enableCopy: false,
                  showScore: true,
                  shuffleQuestions: false,
                  blurVideo: false,
                }}
            />
          }
          toggle={() => setIsTemplateCreateOpen(false)}
      />
    </>
  );
};
